import React from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useRole, UserRole } from "../hooks/useRole";

/**
 * RequirePermission — route guard on RBAC grants, not role labels.
 *
 * The counterpart to RequireRoles: that one asks "is your users.role in this
 * list", this one asks "does your role's permission set include this code". A
 * page whose GET is gated on `patients.view` server-side must be unreachable
 * without that grant *from anywhere* — sidebar, deep link, typed URL, browser
 * history — otherwise revoking View Patients only hides the nav entry.
 *
 * Renders a denial panel, NOT a redirect. RequireRoles can redirect because its
 * fallback destination (/patients for an Admin) is always reachable by that role.
 * Here it is exactly the page that may be forbidden, so redirecting an Admin who
 * lost `patients.view` to /patients would loop forever. A panel also tells the
 * user what happened instead of silently bouncing them somewhere else.
 *
 * Fails OPEN when the server never sent grants (`permissionsKnown === false`,
 * i.e. a backend predating permission_codes on /auth/me) — see canAnyOr. The
 * endpoint still enforces; a stale deploy must not lock a clinic out of its own
 * patient list.
 *
 *   <Route element={<RequirePermission codes={["patients.view"]} label="Patients" />}>
 *     <Route path="/patients" element={<PatientListPage />} />
 *   </Route>
 */
interface RequirePermissionProps {
  /** Any one of these grants admits the user (mirrors require_any_permission). */
  codes: string[];
  /** Human name of the area, used in the denial copy. */
  label?: string;
  /**
   * Who got in before RBAC, used only when the server sent no grants. Omit for
   * an area every clinic role could reach (Patients, Appointments); pass it for
   * one that was role-gated, e.g. ["Admin"] for Doctor Management, so an older
   * backend does not suddenly open it to everyone.
   */
  legacyRoles?: UserRole[];
}

export const AccessDenied: React.FC<{ label?: string; detail?: string }> = ({
  label,
  detail,
}) => {
  const navigate = useNavigate();
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f5f5f7] px-4">
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-50">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#d97706"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>
        <h1 className="mb-2 text-lg font-semibold text-slate-800">
          {label ? `No access to ${label}` : "No access"}
        </h1>
        <p className="mb-6 text-sm leading-relaxed text-slate-500">
          {detail ??
            "Your role does not include this permission. Ask a clinic admin to grant it in Settings → Access & Personalizations."}
        </p>
        <button
          type="button"
          onClick={() => navigate("/dashboard", { replace: true })}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
        >
          Back to dashboard
        </button>
      </div>
    </div>
  );
};

const RequirePermission: React.FC<RequirePermissionProps> = ({ codes, label, legacyRoles }) => {
  const { canAnyOr, isLoaded, role } = useRole();

  // Still resolving — render nothing rather than flashing a denial at a user who
  // does hold the grant.
  if (!isLoaded) return null;

  if (canAnyOr(codes, legacyRoles ? legacyRoles.includes(role) : true)) return <Outlet />;

  return <AccessDenied label={label} />;
};

export default RequirePermission;
